/**
 * payment.js — Logica di pagamento: Stripe, POS e contanti.
 * Mirror di payReservation() / notPayReservation() in pay_page_widget.dart.
 * Dipende da: env.js, state.js, validators.js, api.js, stripe.js, ui.js
 */

// ── Wiring bottoni di pagamento ─────────────────────────────────

function wireClientFields() {
    const fields = {
        clientNome:      'name',
        clientCognome:   'lastname',
        clientIndirizzo: 'address',
        clientEmail:     'email',
        clientTelefono:  'phone',
    };
    for (const [elId, field] of Object.entries(fields)) {
        const el = document.getElementById(elId);
        if (el) el.addEventListener('input', () => { BookingState.client[field] = el.value; });
    }
    const cfEl = document.getElementById('clientCF');
    if (cfEl) cfEl.addEventListener('input', () => {
        cfEl.value = cfEl.value.toUpperCase();
        BookingState.client.cf = cfEl.value;
    });
    const privEl = document.getElementById('clientPrivacy');
    if (privEl) privEl.addEventListener('change', () => {
        BookingState.client.privacy = privEl.checked ? '1' : '0';
    });
}

// Riscrive i 3 onclick="submitForm()" dell'HTML con i giusti handler
function rewirePaymentButtons() {
    const btnPagaOra = document.querySelector('#step-2 .btn-avanti-dark:not(#btnConfirmStripe)');
    const btnBorders = document.querySelectorAll('#step-2 .btn-border-dark.btn-tratta');

    if (btnPagaOra) {
        btnPagaOra.removeAttribute('onclick');
        btnPagaOra.addEventListener('click', onPayNow);
    }
    if (btnBorders[0]) {
        btnBorders[0].removeAttribute('onclick');
        btnBorders[0].addEventListener('click', () => payOnVan(true));   // POS
    }
    if (btnBorders[1]) {
        btnBorders[1].removeAttribute('onclick');
        btnBorders[1].addEventListener('click', () => payOnVan(false));  // CASH
    }
}

// ── Azioni di pagamento ─────────────────────────────────────────

// "Paga ora" — mostra il form Stripe dopo validazione
function onPayNow() {
    collectClientDataFromDOM();
    const err = Validators.clientData();
    if (err) { alert(err); return; }
    if (!readyForPayment) {
        alert('Preventivo non ancora calcolato. Attendi il completamento prima di procedere.');
        return;
    }
    const stripeBox = document.getElementById('stripeBox');
    if (stripeBox) {
        stripeBox.classList.remove('d-none');
        stripeBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (ENV.USE_STRIPE_PAYMENT_ELEMENT) {
            if (!StripePaymentElementController.initialized) {
                loadStripeJS(() => {
                    StripePaymentElementController.init(totalToCents(BookingState.Summary?.total));
                });
            }
        } else {
            if (!StripeController.initialized) StripeController.init();
        }
    }
    track('booking_pay_now_click', {
        reservation_type: BookingState.reservation_type,
        total:            BookingState.Summary?.total || '',
    });
}

// Tipi Stripe che richiedono redirect (PayPal, iDEAL, Bancontact, …)
const REDIRECT_PM_TYPES = new Set(['paypal','ideal','bancontact','sofort','giropay','p24','eps','przelewy24']);

// Conferma pagamento Stripe → crea PaymentMethod → smista su redirect vs carta
async function onStripeConfirm() {
    if (isInPayment) return;

    const errEl = document.getElementById('stripe-card-errors');
    if (errEl) errEl.textContent = '';

    collectClientDataFromDOM();
    const clientErr = Validators.clientData();
    if (clientErr) {
        if (errEl) errEl.textContent = clientErr;
        return;
    }

    isInPayment = true;
    setPaymentBusy(true);

    const activeController = ENV.USE_STRIPE_PAYMENT_ELEMENT
        ? StripePaymentElementController
        : StripeController;
    const confirmLabel = ENV.USE_STRIPE_PAYMENT_ELEMENT
        ? 'Conferma pagamento'
        : 'Conferma pagamento con carta';

    const stripeBtn = document.getElementById('btnConfirmStripe');
    if (stripeBtn) stripeBtn.textContent = 'Elaborazione pagamento...';

    try {
        const pm = await activeController.createPaymentMethod();  // { id, type }
        BookingState.PaymentMethod = pm.id;

        if (REDIRECT_PM_TYPES.has(pm.type)) {
            // Metodo redirect: crea PI lato server, poi stripe.confirmPayment gestisce il redirect
            BookingState.paymentMethodLabel = pm.type.charAt(0).toUpperCase() + pm.type.slice(1);
            await executeRedirectPayment(pm.id, pm.type);
        } else {
            // Carta / SEPA / wallet: flusso diretto come prima
            BookingState.paymentMethodLabel = 'Carta di credito';
            await executePayment(buildPayload(pm.id, false));
        }
    } catch (err) {
        if (errEl) errEl.textContent = err.message;
        isInPayment = false;
        setPaymentBusy(false);
        if (stripeBtn) stripeBtn.textContent = confirmLabel;
    }
}

// Paga a bordo van (POS o contanti) — is_await_for_paymant = 1
async function payOnVan(withCard) {
    if (isInPayment) return;
    collectClientDataFromDOM();
    const err = Validators.clientData();
    if (err) { alert(err); return; }
    if (!readyForPayment) {
        alert('Preventivo non ancora calcolato. Attendi prima di procedere.');
        return;
    }
    isInPayment = true;
    setPaymentBusy(true);
    BookingState.paymentMethodLabel = withCard ? 'POS' : 'Contanti';
    await executePayment(buildPayload(null, true, withCard));
}

// ── Costruzione payload e chiamata API ──────────────────────────

function buildPayload(stripePaymentMethodId = null, onVan = false, withCard = false) {
    const d = { ...BookingState.Summary };
    d.client     = { ...BookingState.client };
    d.fly_number = BookingState.fly_number;
    d.train_name = BookingState.train_name;
    d.boat_name  = BookingState.boat_name;

    if (onVan) {
        d.payment_method       = withCard ? 'POS' : 'CASH';
        d.is_await_for_paymant = 1;
        d.PaymentMethod        = '';
    } else {
        d.PaymentMethod = stripePaymentMethodId || BookingState.PaymentMethod;
    }
    return d;
}

// ── Pagamento redirect (PayPal, iDEAL, …) ──────────────────────
// 1. Chiama initRedirectPayment: crea un PaymentIntent NON confermato.
// 2. Salva tutto in localStorage.
// 3. Chiama stripe.confirmPayment() che gestisce nativamente il redirect
//    (PayPal popup/redirect, iDEAL, ecc.) e poi torna su return_url.
// La prenotazione viene registrata al rientro da init.js.
async function executeRedirectPayment(pmId, pmType) {
    track('booking_payment_initiated', {
        reservation_type: BookingState.reservation_type,
        payment_method:   BookingState.paymentMethodLabel || '',
        total:            BookingState.Summary?.total || '',
    });

    const payload = { ...BookingState.Summary };
    payload.client              = { ...BookingState.client };
    payload.PaymentMethod       = pmId;
    payload.PaymentMethodType   = pmType || 'paypal';

    const result = await api.execItem('outreach.initRedirectPayment', payload, 'cron:1');

    if (!result?.result?.client_secret) {
        isInPayment = false;
        setPaymentBusy(false);
        const msg = result?.result?.errorInfo || 'Errore nell\'avvio del pagamento. Riprova.';
        const errEl = document.getElementById('stripe-card-errors');
        if (errEl) errEl.textContent = msg;
        const stripeBtn = document.getElementById('btnConfirmStripe');
        if (stripeBtn) stripeBtn.textContent = 'Conferma pagamento';
        return;
    }

    // Salva l'intero stato in localStorage PRIMA del redirect
    localStorage.setItem('pendingRedirectPayment', JSON.stringify({
        bookingState:       { ...BookingState, directionsData: null },
        paymentIntentId:    result.result.payment_intent_id,
        reservationType:    BookingState.reservation_type,
        paymentMethodLabel: BookingState.paymentMethodLabel || '',
        total:              BookingState.Summary?.total || '',
    }));

    // stripe.confirmPayment gestisce il redirect a PayPal / iDEAL / ecc.
    const stripeInstance = ENV.USE_STRIPE_PAYMENT_ELEMENT
        ? StripePaymentElementController.stripe
        : StripeController.stripe;

    const { error } = await stripeInstance.confirmPayment({
        clientSecret: result.result.client_secret,
        confirmParams: {
            return_url: window.location.origin + window.location.pathname,
        },
    });

    // Se arriva qui il redirect non è partito → mostra l'errore
    localStorage.removeItem('pendingRedirectPayment');
    isInPayment = false;
    setPaymentBusy(false);
    const errEl = document.getElementById('stripe-card-errors');
    if (errEl) errEl.textContent = error?.message || 'Errore avvio pagamento. Riprova.';
    const stripeBtn = document.getElementById('btnConfirmStripe');
    if (stripeBtn) stripeBtn.textContent = 'Conferma pagamento';
}

// ── Pagamento diretto (carte, SEPA, wallet) ─────────────────────
// Chiama paymentReservation: crea prenotazione + addebita subito.
async function executePayment(payload) {
    track('booking_payment_initiated', {
        reservation_type: BookingState.reservation_type,
        payment_method:   BookingState.paymentMethodLabel || '',
        total:            BookingState.Summary?.total || '',
    });
    const result = await api.execItem('outreach.paymentReservation', payload, 'cron:1');

    if (result?.result?.reservation) {
        track('booking_payment_success', {
            reservation_type: BookingState.reservation_type,
            payment_method:   BookingState.paymentMethodLabel || '',
            total:            BookingState.Summary?.total || '',
            reservation_id:   result.result.reservation?.id || '',
        });
        isInPayment     = false;
        readyForPayment = false;
        readyForSummary = false;
        BookingState.Summary = {};
        setPaymentBusy(false);
        onPaymentSuccess();
    } else {
        isInPayment = false;
        setPaymentBusy(false);
        const msg = result?.result?.errorInfo
            || 'Errore durante il pagamento. Controlla i dati e riprova.';
        showStatusAlert(msg, true);
    }
}

// ── UI helpers di pagamento ─────────────────────────────────────

function setPaymentBusy(busy) {
    document.querySelectorAll(
        '#step-2 .btn-avanti-dark:not(#btnConfirmStripe), #step-2 .btn-border-dark, #btnConfirmStripe'
    ).forEach(b => { b.disabled = busy; });
}

function onPaymentSuccess() {
    showThankYouPage();
}

// Legge i campi di fatturazione dal DOM e aggiorna BookingState.client
function collectClientDataFromDOM() {
    const fields = {
        clientNome:      'name',
        clientCognome:   'lastname',
        clientCF:        'cf',
        clientIndirizzo: 'address',
        clientEmail:     'email',
        clientTelefono:  'phone',
    };
    for (const [elId, field] of Object.entries(fields)) {
        const el = document.getElementById(elId);
        if (el) BookingState.client[field] = el.value || '';
    }
    BookingState.client.cf      = BookingState.client.cf.toUpperCase();
    BookingState.client.privacy = document.getElementById('clientPrivacy')?.checked ? '1' : '0';
}

// Alias per retrocompatibilità con onclick="submitForm()" rimasto nell'HTML
function submitForm() {
    onPayNow();
}
